import React, { useState, useEffect, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select, Typography, Divider, Tabs, Tab, Box, Chip, Tooltip } from "@material-ui/core";
import { Visibility, VisibilityOff, InfoOutlined } from "@material-ui/icons";
import { InputAdornment, IconButton, Paper } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContextHelpButton from "../ContextHelpButton";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    sectionDivider: {
        marginBottom: theme.spacing(1),
    },
    helperChip: {
        margin: theme.spacing(0.5),
        fontSize: "0.7rem",
    },
    variablesBox: {
        padding: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
        borderRadius: 8,
    },
    tabPanel: {
        padding: theme.spacing(1, 0),
    },
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para Inteligência Artificial"),
    voice: Yup.string().required("Informe o modo para Voz"),
    max_tokens: Yup.number().required("Informe o número máximo de tokens"),
    temperature: Yup.number().required("Informe a temperatura"),
    apikey: Yup.string().required("Informe a API Key"),
    queueId: Yup.number().required("Informe a fila"),
    max_messages: Yup.number().required("Informe o número máximo de mensagens")
});

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ paddingTop: 8 }}>
        {value === index && children}
    </div>
);

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10,
        greetingMessage: "",
        model: "gpt-4.1-mini",
        provider: "openai",
        transferMessage: "",
        errorMessage: "",
        transferKeywords: "",
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => {
                    return { ...prevState, ...data };
                });
                setSelectedVoice(data.voice);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto");
        setActiveTab(0);
        onClose();
    };

    const handleChangeVoice = (e) => {
        setSelectedVoice(e.target.value);
    };

    const handleSavePrompt = async values => {
        const promptData = { ...values, voice: selectedVoice };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, v) => setActiveTab(v)}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    variant="fullWidth"
                                    style={{ marginBottom: 8 }}
                                >
                                    <Tab label="⚙️ Geral" />
                                    <Tab label="🤖 IA & Modelo" />
                                    <Tab label="💬 Mensagens" />
                                    <Tab label="🔊 Voz" />
                                </Tabs>

                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    padding: "4px 8px 2px"
                                  }}
                                >
                                  <ContextHelpButton
                                    contextKey={activeTab === 1 ? "prompts.aimodel" : activeTab === 2 ? "prompts.messages" : activeTab === 3 ? "prompts.voice" : "prompts.general"}
                                    mode="inline"
                                    tooltip="Ajuda sobre esta aba"
                                    dialogTitle="Ajuda do Talk.Ai"
                                  />
                                </div>

                                {/* ═══ ABA GERAL ═══ */}
                                <TabPanel value={activeTab} index={0}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.name")}
                                        name="name"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                    />
                                    <QueueSelectSingle />

                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <Field
                                            as={TextField}
                                            label="API Key"
                                            name="apiKey"
                                            type={showApiKey ? 'text' : 'password'}
                                            error={touched.apiKey && Boolean(errors.apiKey)}
                                            helperText={touched.apiKey && errors.apiKey}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={handleToggleApiKey}>
                                                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </TabPanel>

                                {/* ═══ ABA IA & MODELO ═══ */}
                                <TabPanel value={activeTab} index={1}>
                                    <div className={classes.multFieldLine}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Provedor</InputLabel>
                                            <Field as={Select} name="provider" label="Provedor" labelWidth={70}>
                                                <MenuItem value="openai">OpenAI</MenuItem>
                                                <MenuItem value="gemini">Google Gemini</MenuItem>
                                            </Field>
                                        </FormControl>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>Modelo</InputLabel>
                                            <Field as={Select} name="model" label="Modelo" labelWidth={55}>
                                                {values.provider === "openai" ? [
                                                    <MenuItem key="gpt-4.1-mini" value="gpt-4.1-mini">GPT-4.1 Mini (Recomendado)</MenuItem>,
                                                    <MenuItem key="gpt-4.1" value="gpt-4.1">GPT-4.1</MenuItem>,
                                                    <MenuItem key="gpt-4.1-nano" value="gpt-4.1-nano">GPT-4.1 Nano (Econômico)</MenuItem>,
                                                    <MenuItem key="gpt-4o" value="gpt-4o">GPT-4o</MenuItem>,
                                                    <MenuItem key="gpt-4o-mini" value="gpt-4o-mini">GPT-4o Mini</MenuItem>,
                                                    <MenuItem key="gpt-3.5-turbo" value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>,
                                                ] : [
                                                    <MenuItem key="gemini-2.5-flash" value="gemini-2.5-flash">Gemini 2.5 Flash (Recomendado)</MenuItem>,
                                                    <MenuItem key="gemini-2.5-pro" value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>,
                                                    <MenuItem key="gemini-2.0-flash" value="gemini-2.0-flash">Gemini 2.0 Flash</MenuItem>,
                                                    <MenuItem key="gemini-1.5-flash" value="gemini-1.5-flash">Gemini 1.5 Flash</MenuItem>,
                                                    <MenuItem key="gemini-1.5-pro" value="gemini-1.5-pro">Gemini 1.5 Pro</MenuItem>,
                                                ]}
                                            </Field>
                                        </FormControl>
                                    </div>

                                    <Field
                                        as={TextField}
                                        label="Treinamento / Prompt do Sistema"
                                        name="prompt"
                                        error={touched.prompt && Boolean(errors.prompt)}
                                        helperText={touched.prompt && errors.prompt}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                        rows={8}
                                        multiline={true}
                                        placeholder="Ex: Você é um assistente virtual da empresa X. Seu papel é atender clientes, tirar dúvidas sobre produtos e serviços..."
                                    />

                                    <Paper variant="outlined" className={classes.variablesBox}>
                                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                            💡 Variáveis disponíveis no prompt:
                                        </Typography>
                                        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {[
                                                { v: "{{name}}", d: "Nome do contato" },
                                                { v: "{{firstName}}", d: "Primeiro nome" },
                                                { v: "{{queue}}", d: "Nome da fila" },
                                                { v: "{{userName}}", d: "Nome do atendente" },
                                                { v: "{{date}}", d: "Data atual" },
                                                { v: "{{hour}}", d: "Hora atual" },
                                                { v: "{{ms}}", d: "Saudação (Bom dia...)" },
                                            ].map(item => (
                                                <Tooltip key={item.v} title={item.d} arrow>
                                                    <Chip
                                                        size="small"
                                                        label={item.v}
                                                        className={classes.helperChip}
                                                        onClick={() => navigator.clipboard.writeText(item.v)}
                                                        clickable
                                                    />
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </Paper>

                                    <div className={classes.multFieldLine}>
                                        <Field
                                            as={TextField}
                                            label="Temperatura"
                                            name="temperature"
                                            type="number"
                                            error={touched.temperature && Boolean(errors.temperature)}
                                            helperText="0 = preciso, 1 = criativo"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                                        />
                                        <Field
                                            as={TextField}
                                            label="Máx. Tokens"
                                            name="maxTokens"
                                            type="number"
                                            error={touched.maxTokens && Boolean(errors.maxTokens)}
                                            helperText="Tamanho máx. da resposta"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            inputProps={{ min: 50, max: 8000 }}
                                        />
                                        <Field
                                            as={TextField}
                                            label="Máx. Mensagens"
                                            name="maxMessages"
                                            type="number"
                                            error={touched.maxMessages && Boolean(errors.maxMessages)}
                                            helperText="Contexto de memória"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            inputProps={{ min: 1, max: 50 }}
                                        />
                                    </div>
                                </TabPanel>

                                {/* ═══ ABA MENSAGENS ═══ */}
                                <TabPanel value={activeTab} index={2}>
                                    <Field
                                        as={TextField}
                                        label="Mensagem de Saudação Inicial"
                                        name="greetingMessage"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={3}
                                        multiline={true}
                                        placeholder="Ex: Olá {{firstName}}! 👋 Sou o assistente virtual da empresa. Como posso te ajudar?"
                                        helperText="Primeira mensagem enviada ao cliente quando a IA assumir. Deixe vazio para a IA iniciar diretamente."
                                    />

                                    <Field
                                        as={TextField}
                                        label="Mensagem de Transferência"
                                        name="transferMessage"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={2}
                                        multiline={true}
                                        placeholder="Ex: Entendido! Vou te transferir para um atendente humano. Aguarde um momento! 😊"
                                        helperText="Mensagem enviada quando o cliente solicitar falar com um atendente."
                                    />

                                    <Field
                                        as={TextField}
                                        label="Mensagem de Erro"
                                        name="errorMessage"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={2}
                                        multiline={true}
                                        placeholder="Ex: Desculpe, estou com dificuldades técnicas no momento. Tente novamente mais tarde."
                                        helperText="Mensagem enviada quando ocorrer erro na IA."
                                    />

                                    <Divider style={{ margin: "16px 0 8px" }} />
                                    <Typography className={classes.sectionTitle} variant="subtitle2">
                                        🔑 Palavras-chave de Transferência
                                    </Typography>

                                    <Field
                                        as={TextField}
                                        label="Palavras-chave de Transferência"
                                        name="transferKeywords"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={3}
                                        multiline={true}
                                        placeholder="Ex: falar com humano, quero atendente, sair da ia, me ajuda de verdade"
                                        helperText="Palavras ou frases separadas por vírgula. Quando o cliente digitar alguma delas, será transferido para um atendente. As palavras padrão do sistema continuam ativas."
                                    />

                                    <Paper variant="outlined" className={classes.variablesBox}>
                                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                            💡 Variáveis disponíveis nas mensagens:
                                        </Typography>
                                        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {[
                                                { v: "{{name}}", d: "Nome do contato" },
                                                { v: "{{firstName}}", d: "Primeiro nome" },
                                                { v: "{{ms}}", d: "Saudação" },
                                                { v: "{{userName}}", d: "Atendente" },
                                                { v: "{{queue}}", d: "Fila" },
                                            ].map(item => (
                                                <Tooltip key={item.v} title={item.d} arrow>
                                                    <Chip
                                                        size="small"
                                                        label={item.v}
                                                        className={classes.helperChip}
                                                        onClick={() => navigator.clipboard.writeText(item.v)}
                                                        clickable
                                                    />
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </Paper>
                                </TabPanel>

                                {/* ═══ ABA VOZ ═══ */}
                                <TabPanel value={activeTab} index={3}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>Modo de Resposta</InputLabel>
                                        <Select
                                            id="type-select"
                                            labelWidth={130}
                                            name="voice"
                                            value={selectedVoice}
                                            onChange={handleChangeVoice}
                                            multiple={false}
                                        >
                                            <MenuItem value="texto">Texto</MenuItem>
                                            <MenuItem value="pt-BR-FranciscaNeural">Francisca</MenuItem>
                                            <MenuItem value="pt-BR-AntonioNeural">Antônio</MenuItem>
                                            <MenuItem value="pt-BR-BrendaNeural">Brenda</MenuItem>
                                            <MenuItem value="pt-BR-DonatoNeural">Donato</MenuItem>
                                            <MenuItem value="pt-BR-ElzaNeural">Elza</MenuItem>
                                            <MenuItem value="pt-BR-FabioNeural">Fábio</MenuItem>
                                            <MenuItem value="pt-BR-GiovannaNeural">Giovanna</MenuItem>
                                            <MenuItem value="pt-BR-HumbertoNeural">Humberto</MenuItem>
                                            <MenuItem value="pt-BR-JulioNeural">Julio</MenuItem>
                                            <MenuItem value="pt-BR-LeilaNeural">Leila</MenuItem>
                                            <MenuItem value="pt-BR-LeticiaNeural">Letícia</MenuItem>
                                            <MenuItem value="pt-BR-ManuelaNeural">Manuela</MenuItem>
                                            <MenuItem value="pt-BR-NicolauNeural">Nicolau</MenuItem>
                                            <MenuItem value="pt-BR-ValerioNeural">Valério</MenuItem>
                                            <MenuItem value="pt-BR-YaraNeural">Yara</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div className={classes.multFieldLine}>
                                        <Field
                                            as={TextField}
                                            label="Chave de Voz (Azure)"
                                            name="voiceKey"
                                            error={touched.voiceKey && Boolean(errors.voiceKey)}
                                            helperText={touched.voiceKey && errors.voiceKey}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Field
                                            as={TextField}
                                            label="Região de Voz (Azure)"
                                            name="voiceRegion"
                                            error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                            helperText={touched.voiceRegion && errors.voiceRegion}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </div>
                                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
                                        ⚠️ A resposta por voz funciona apenas com o provedor OpenAI. Com Gemini, a resposta será sempre em texto.
                                    </Typography>
                                </TabPanel>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;
